import React, { useEffect, useState, useRef } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ClickAwayListener,
  ButtonBase,
} from '@mui/material';
import {
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Link as LinkIcon,
  InsertDriveFile as FileIcon,
  Public as PublicIcon,
  Group as GroupIcon,
  Groups as OtableWideIcon,
  Lock as PrivateIcon,
  Send as SendIcon,
  Close as CloseIcon,
  CampaignOutlined as AnnouncementIcon,
} from '@mui/icons-material';
import { Checkbox, FormControlLabel, Tooltip as MuiTooltip } from '@mui/material';
import { useMentions } from './mentions';
import { styled, useTheme } from '@mui/material/styles';
import { commonStyles } from 'shared/styles/enhancedTheme';
import { useUser } from 'shared/helpers/axios';
import EditableLinkPreview from 'components/common/EditableLinkPreview';
import useLinkPreview from 'shared/hooks/useLinkPreview';
import LabMarkdown from 'components/collab/lab/LabMarkdown';
import MarkdownToolbar, { useMarkdownEditing } from 'components/common/MarkdownToolbar';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { reflectionSavedMessage, savePostAsReflection } from 'shared/helpers/reflectionFromPost';
import { POST_MAX_LENGTH, hashtagsOf, lengthHint, postMarkdown, withLink } from 'shared/helpers/postText';

const OpinionCard = styled(Card)(({ theme }) => ({
  ...commonStyles.opinionCard,
  marginBottom: 0,                // the page sets the space to the feed below
}));

const AttachmentChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  '& .MuiChip-deleteIcon': {
    fontSize: '18px',
  },
}));

const OpinionSharingCard = ({ onPost, placeholder = "Share your thoughts..." }) => {
  const theme = useTheme(); 
  const [content, setContent] = useState('');
  // collapsed to one line while not writing: the feed comes first
  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [visibility, setVisibility] = useState('otable');
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  // Posts are markdown: [text](link) shows the text linked to the page, plus bold, lists…
  const [showPreview, setShowPreview] = useState(false);
  const inputRef = useRef(null);
  const [posting, setPosting] = useState(false);

  const { user } = useUser();
  const { showSnackbar = () => {} } = useSnackbar() || {};
  // hashtags in what is being written: ticked, the post is also saved as a reflection with those tags
  const tags = hashtagsOf(content);
  const [asReflection, setAsReflection] = useState(false);

  // the link card: a pasted bare address is replaced by its card (shared/hooks/useLinkPreview)
  const { linkPreview, previewLoading, dismissCard, reset: resetLinkPreview } = useLinkPreview(content, setContent);

  // the formatting buttons under the text box (components/common/MarkdownToolbar)
  const editing = useMarkdownEditing(content, setContent, inputRef, () => setShowPreview(false));

  // who sees the post; the chip cycles through them (the server's Post.VISIBILITY_CHOICES)
  const visibilityOptions = [
    { value: 'otable', label: 'Everyone', icon: <OtableWideIcon /> },
    { value: 'public', label: 'Public', icon: <PublicIcon /> },
    { value: 'connections', label: 'My connections', icon: <GroupIcon /> },
    { value: 'private', label: 'Only me', icon: <PrivateIcon /> }
  ];

  const attachmentTypes = [
    { 
      type: 'image', 
      label: 'Image', 
      icon: <ImageIcon />, 
      accept: 'image/*',
      description: 'Add photos or screenshots'
    },
    { 
      type: 'video', 
      label: 'Video', 
      icon: <VideoIcon />, 
      accept: 'video/*',
      description: 'Share video content'
    },
    { 
      type: 'document', 
      label: 'Document', 
      icon: <FileIcon />, 
      accept: '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx',
      description: 'Upload documents or presentations'
    }
  ];

  const handleFileSelect = (type, accept) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = type !== 'video'; // Allow multiple files except for video
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      const newAttachments = files.map(file => ({
        id: Date.now() + Math.random(),
        type,
        file,
        name: file.name,
        size: file.size,
        url: URL.createObjectURL(file)
      }));
      
      setAttachments(prev => [...prev, ...newAttachments]);
      setAttachmentDialogOpen(false);
    };
    
    input.click();
  };

  const handleAddLink = () => {
    if (linkInput.trim()) {
      const newLink = {
        id: Date.now(),
        type: 'link',
        url: linkInput.trim(),
        name: linkInput.trim(),
        size: 0
      };
      
      setAttachments(prev => [...prev, newLink]);
      setLinkInput('');
      setLinkDialogOpen(false);
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => {
      const updated = prev.filter(att => att.id !== id);
      // Revoke object URLs to prevent memory leaks
      const toRemove = prev.find(att => att.id === id);
      if (toRemove && toRemove.url && toRemove.type !== 'link') {
        URL.revokeObjectURL(toRemove.url);
      }
      return updated;
    });
  };

  // "@…" while writing: a list of people to mention (connections first); picked names stay plain "@Name" in the
  // text and become profile links when posted (./mentions.jsx)
  const mentions = useMentions(content, setContent, inputRef);

  const handlePost = async () => {
    if (!content.trim() && attachments.length === 0) {
      return;
    }

    setPosting(true);
    
    try {
      const postData = {
        content: mentions.apply(content.trim()),
        attachments: attachments.map(att => ({
          type: att.type,
          name: att.name,
          size: att.size,
          url: att.type === 'link' ? att.url : undefined,
          file: att.type !== 'link' ? att.file : undefined
        })),
        visibility,
        is_announcement: isAnnouncement,
        link_preview: linkPreview || null,
        timestamp: new Date().toISOString(),
        author: {
          id: user.id,
          name: user.name || user.username,
          avatar: user.avatar
        }
      };

      // Call the onPost callback if provided
      let created = null;
      if (onPost) {
        created = await onPost(postData);
      }
      if (asReflection && tags.length) {
        try {
          showSnackbar(reflectionSavedMessage(await savePostAsReflection({
            body: withLink(content.trim(), linkPreview?.url), tags, date: new Date(), postId: created?.id,
          })), 'success');
          // the feed shows the new post with "Open the (draft) reflection", not "Add as a reflection"
          window.dispatchEvent(new CustomEvent('postCreated', { detail: { post: created } }));
        } catch {
          showSnackbar('Posted, but the reflection could not be saved.', 'error');
        }
      }
      setAsReflection(false);
      mentions.reset();

      // Reset form
      setOpen(false);
      setContent('');
      setAttachments([]);
      setVisibility('otable');
      setIsAnnouncement(false);
      setShowPreview(false);
      resetLinkPreview();
      
    } catch (error) {
      console.error('Error posting opinion:', error);
    } finally {
      setPosting(false);
    }
  };

  // the editor gets the cursor as it opens; it closes again when left without anything written
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  const blank = !content.trim() && attachments.length === 0 && !showPreview;
  const closeIfBlank = () => {
    if (blank && !attachmentDialogOpen && !linkDialogOpen && !posting) setOpen(false);
  };

  const getAttachmentIcon = (type) => {
    switch (type) {
      case 'image': return <ImageIcon />;
      case 'video': return <VideoIcon />;
      case 'document': return <FileIcon />;
      case 'link': return <LinkIcon />;
      default: return <FileIcon />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!open) {
    return (
      // one slim line: the card's usual padding and bottom margin would leave a tall empty box
      <OpinionCard sx={{ p: 0.75, mb: 0 }}>
        <CardContent sx={{ px: { xs: 1.5, sm: 2 }, py: '10px !important', display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          <Avatar src={user?.avatar || undefined} alt={user?.username || 'User'}
            sx={{ width: 30, height: 30, backgroundColor: theme.palette.primary.avatar, color: theme.palette.primary.contrastText }} />
          <ButtonBase onClick={() => setOpen(true)} aria-label="Write a post"
            sx={{ flex: 1, justifyContent: 'flex-start', textAlign: 'left', px: 1.75, py: 1.1, borderRadius: '12px',
              border: '1px solid', borderColor: 'primary.ulight', color: 'text.secondary', fontSize: '1.4rem',
              transition: 'border-color .15s, background-color .15s',
              '&:hover, &:focus-visible': { borderColor: 'primary.light', bgcolor: 'action.hover' } }}>
            {placeholder}
          </ButtonBase>
        </CardContent>
      </OpinionCard>
    );
  }

  return (
    <>
      <ClickAwayListener onClickAway={closeIfBlank}>
      <OpinionCard>
        <CardContent sx={{ border: 'none', px: { xs: 1.5, sm: 2 } }}>
          {/* User Avatar and Input */}
          <Box display="flex" gap={{ xs: 1, sm: 2 }} mb={2} sx={{ border: 'none', minWidth: 0 }}>
      

    {user?.avatar  ? (
                  <Avatar
                    src={user.avatar}
                    
                    sx={{ 
                      width: 30,
                      height: 30,
                      backgroundColor: theme.palette.primary.avatar,
                      color: theme.palette.primary.contrastText,
                      fontSize: 14,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      // mr: 1,
                     }}
                  />
                ) : 
                (
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    backgroundColor: theme.palette.primary.avatar,
                    color: theme.palette.primary.contrastText,
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // mr: 1,
                  }}
                  alt={user?.username || 'User'}
                >
                  {/* {!user?.avatar && (user?.username?.[0] || 'U').toUpperCase()} */}
                </Avatar>

)}

<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>{/* minWidth 0: never wider than the card */}
        
            <TextField
  fullWidth
  multiline
  minRows={3}
  maxRows={8}
  inputProps={{ maxLength: POST_MAX_LENGTH }}
  helperText={lengthHint(content)}
  placeholder={placeholder}
  value={content}
  inputRef={inputRef}
  {...mentions.field}
  style={showPreview ? { display: 'none' } : undefined}
  variant="outlined"
  sx={{
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '12px',
      
      // Add border to root element
      border: '1px solid',
      borderColor: 'primary.ulight',
      
      
      '&.Mui-focused': {
        backgroundColor: 'white',
        borderColor: 'primary.light', // Focus border color
      }
    },
    
    // Hide the default outline
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    }
  }}
/>

          {mentions.list}

          {/* The post as it will look (markdown) while previewing, and the formatting buttons */}
          {showPreview && (
            <Box sx={{ minHeight: 86, px: 1.75, py: 1.25, borderRadius: '12px', border: '1px solid', borderColor: 'primary.ulight',
              wordBreak: 'break-word' }}>
              {content.trim()
                ? <LabMarkdown images hashtags>{postMarkdown(content)}</LabMarkdown>
                : <Typography variant="body2" color="text.secondary">Nothing to preview yet.</Typography>}
            </Box>
          )}
          <MarkdownToolbar editing={editing} preview={showPreview} onTogglePreview={() => setShowPreview(!showPreview)}>
            {tags.length > 0 && (
              <FormControlLabel
                control={<Checkbox size="small" checked={asReflection} onChange={(e) => setAsReflection(e.target.checked)} sx={{ p: 0.5 }} />}
                label="Add as a reflection"
                sx={{ mr: 0.5, ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
              />
            )}
          </MarkdownToolbar>


{/* Link Preview: as the post will show it */}
          <EditableLinkPreview linkPreview={linkPreview} previewLoading={previewLoading} onDismiss={dismissCard} />
      </Box>

          </Box>

       

          {/* Attachments Display */}
          {attachments.length > 0 && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Attachments ({attachments.length})
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {attachments.map((attachment) => (
                  <AttachmentChip
                    key={attachment.id}
                    icon={getAttachmentIcon(attachment.type)}
                    label={`${attachment.name} ${formatFileSize(attachment.size)}`}
                    onDelete={() => removeAttachment(attachment.id)}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Action Buttons */}
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            {/* narrow screens: the buttons wrap instead of overlapping */}
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} useFlexGap>
              {/* Attachment Button */}
              {/* <IconButton
                onClick={() => setAttachmentDialogOpen(true)}
                color="primary"
                size="small"
                aria-label="Add attachment"
              >
                <AddIcon />
              </IconButton> */}

              {/* Link Button */}
              {/* <IconButton
                onClick={() => setLinkDialogOpen(true)}
                color="primary"
                size="small"
                aria-label="Add link"
              >
                <LinkIcon />
              </IconButton> */}



{/* Announcement toggle */}
<MuiTooltip
  arrow
  placement="top"
  title={
    <Box sx={{ p: 0.5, maxWidth: 260 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
        Announcements are for:
      </Typography>
      {[
        'Job & position openings in your research project',
        'Events — conferences, seminars, workshops, webinars',
        'Grant deadlines or major funding news',
        'Dataset or tool releases relevant to the community',

      ].map((line) => (
        <Typography key={line} variant="caption" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.3, lineHeight: 1.4 }}>
          <Box component="span" sx={{ color: 'success.light', fontWeight: 700, flexShrink: 0 }}>✓</Box>
          {line}
        </Typography>
      ))}
      <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
        <Typography variant="caption" sx={{  display: 'flex', alignItems: 'flex-start', gap: 0.5, lineHeight: 1.4 }}>
          <Box component="span" sx={{ color: 'error.light', fontWeight: 700, flexShrink: 0 }}>⚠</Box>
          Irrelevant or promotional content will be reported and removed by staff.
        </Typography>
      </Box>
    </Box>
  }
>
  <Chip
    data-guide="announcement"
    label="Announcement"
    icon={<AnnouncementIcon fontSize="small" />}
    variant={isAnnouncement ? 'filled' : 'outlined'}
    onClick={() => setIsAnnouncement(!isAnnouncement)}
    size="small"


    sx={{
      cursor: 'pointer',
      ml: 0.5,
      ...(isAnnouncement
        ? {
            backgroundColor: 'primary.light',
            color: '#fff',
            '& .MuiChip-icon': { color: '#fff' },
            '&:hover': { backgroundColor: 'primary.light' },
          }
        : {
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'action.hover' },
          }),
    }}
  />
</MuiTooltip>

<Chip
  icon={
    <Typography variant="caption" color='text.secondary'>
      {visibilityOptions.find(opt => opt.value === visibility)?.icon}
    </Typography>
  }
  label={
    <Typography variant="caption" color='text.secondary'>
      {visibilityOptions.find(opt => opt.value === visibility)?.label}
    </Typography>
  }
  size="small"
  variant="outlined"
  onClick={() => {
    const currentIndex = visibilityOptions.findIndex(opt => opt.value === visibility);
    const nextIndex = (currentIndex + 1) % visibilityOptions.length;
    setVisibility(visibilityOptions[nextIndex].value);
  }}
/>

            {/* Post Button */}
            <Button
              variant="contained"
              startIcon={posting ? null : <SendIcon />}
              onClick={handlePost}
              disabled={(!content.trim() && attachments.length === 0) || posting}
              sx={{ borderRadius: '20px', minWidth: '100px' }}
            >
              {posting ? (
                <LinearProgress 
                  size={20} 
                  sx={{ 
                    width: '60px',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 'white'
                    }
                  }} 
                />
              ) : (
                'Post'
              )}
            </Button>
            </Stack>
          </Box>
        </CardContent>
      </OpinionCard>
      </ClickAwayListener>

      {/* Attachment Type Selection Dialog */}
      <Dialog
        open={attachmentDialogOpen}
        onClose={() => setAttachmentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Attachment
          <IconButton
            aria-label="close"
            onClick={() => setAttachmentDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <List>
            {attachmentTypes.map((type) => (
              <ListItem
                key={type.type}
                button
                onClick={() => handleFileSelect(type.type, type.accept)}
                sx={{ borderRadius: 2, mb: 1 }}
              >
                <ListItemIcon>{type.icon}</ListItemIcon>
                <ListItemText
                  primary={type.label}
                  secondary={type.description}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* Link Addition Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            variant="outlined"
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            placeholder="https://example.com"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddLink} 
            variant="contained"
            disabled={!linkInput.trim()}
          >
            Add Link
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OpinionSharingCard;